import React, { useState } from 'react';
import { Button, Form, Modal, Select, Table, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import {
  DeleteNetworkTemplateDocument,
  ListNetworkTemplatesQuery,
  useCreateNetworkTemplateMutation,
  useListEmailTemplatesQuery,
  useListLetterTemplatesQuery,
  useListNetworkTemplatesQuery,
  useListSmsTemplatesQuery,
} from '../../graphql/generated';
import LanguageSelect, { getLanguageLabel } from '../utils/LanguageSelect';
import MutateButton from '../utils/MutateButton';

export const TEMPLATE_TYPE_OPTIONS = ['letter', 'email', 'sms'];
export const TEMPLATE_FAMILY_OPTIONS = ['SVA', 'BNL'];

// antd's column `width` is only a hint under auto table layout; a real
// min-width keeps short names from collapsing the columns while still
// letting long ones expand.
const withMinWidth = (px: number) => ({
  onHeaderCell: () => ({ style: { minWidth: px } }),
  onCell: () => ({ style: { minWidth: px } }),
});

type NetworkTemplateListItem = ListNetworkTemplatesQuery['network_templates'][number];

const templateName = (record: NetworkTemplateListItem) =>
  record.letter_template?.letter_template_name ??
  record.email_template?.email_template_name ??
  record.sms_template?.sms_template_name;

const NetworkApprovedTemplates: React.FC<{ network_id: string }> = ({
  network_id,
}) => {
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<string>();
  const [addForm] = Form.useForm();

  const { data, loading } = useListNetworkTemplatesQuery({
    variables: { network_id },
    notifyOnNetworkStatusChange: true,
  });

  const letterTemplatesQuery = useListLetterTemplatesQuery({
    skip: !addModalVisible,
  });
  const emailTemplatesQuery = useListEmailTemplatesQuery({
    skip: !addModalVisible,
  });
  const smsTemplatesQuery = useListSmsTemplatesQuery({
    skip: !addModalVisible,
  });

  const [
    createNetworkTemplate,
    createNetworkTemplateResult,
  ] = useCreateNetworkTemplateMutation({
    refetchQueries: ['listNetworkTemplates'],
  });

  const templateOptions =
    selectedType === 'letter'
      ? (letterTemplatesQuery.data?.letter_templates || []).map((t) => ({
          value: t.letter_template_id,
          label: t.letter_template_name || t.letter_template_id,
        }))
      : selectedType === 'email'
      ? (emailTemplatesQuery.data?.email_templates || []).map((t) => ({
          value: t.email_template_id,
          label: t.email_template_name || t.email_template_id,
        }))
      : selectedType === 'sms'
      ? (smsTemplatesQuery.data?.sms_templates || []).map((t) => ({
          value: t.sms_template_id,
          label: t.sms_template_name || t.sms_template_id,
        }))
      : [];

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <Table
        dataSource={data?.network_templates || []}
        loading={loading}
        rowKey="network_template_id"
        size="small"
        bordered
        pagination={false}
        columns={[
          {
            title: 'Template',
            ...withMinWidth(535),
            render: (_, record: NetworkTemplateListItem) =>
              templateName(record) ?? (
                <Typography.Text type="danger">
                  {record.template_id}
                </Typography.Text>
              ),
          },
          {
            title: 'Type',
            dataIndex: 'template_type',
            ...withMinWidth(80),
          },
          {
            title: 'Family',
            dataIndex: 'template_family',
            ...withMinWidth(95),
            render: (family) => family || '-',
          },
          {
            title: 'Language',
            dataIndex: 'template_language',
            ...withMinWidth(115),
            render: getLanguageLabel,
          },
          {
            title: '',
            render: (_, record: NetworkTemplateListItem) => (
              <MutateButton
                danger
                icon={<DeleteOutlined />}
                document={DeleteNetworkTemplateDocument}
                variables={{ network_template_id: record.network_template_id }}
                refetchQueries={['listNetworkTemplates']}
                popconfirm={{ title: 'Remove this approved template?' }}
              />
            ),
          },
        ]}
      />
      <Button
        type="primary"
        size="small"
        icon={<PlusOutlined />}
        onClick={() => setAddModalVisible(true)}
      >
        Add
      </Button>
      <Modal
        title="Add Approved Template"
        visible={addModalVisible}
        okText="Add"
        confirmLoading={createNetworkTemplateResult.loading}
        onOk={() => addForm.submit()}
        onCancel={() => setAddModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          preserve={false}
          initialValues={{ template_language: 'EN' }}
          onValuesChange={(changedValues) => {
            if ('template_type' in changedValues) {
              setSelectedType(changedValues.template_type);
              addForm.setFieldsValue({ template_id: undefined });
            }
          }}
          onFinish={(values) => {
            createNetworkTemplate({
              variables: { network_template: { network_id, ...values } },
            })
              .then(() => setAddModalVisible(false))
              .catch((error) => {
                message.error(
                  error.message?.includes(
                    'network_templates_network_type_family_language_key'
                  ) || error.message?.includes('Uniqueness violation')
                    ? 'This network already has an approved template for that type, family, and language'
                    : `Failed to add template: ${error.message}`
                );
              });
          }}
        >
          <Form.Item
            label="Type"
            name="template_type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              options={TEMPLATE_TYPE_OPTIONS.map((t) => ({
                value: t,
                label: t,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Family"
            name="template_family"
            help="Used for packet downloads; leave blank for general approvals"
          >
            <Select
              allowClear
              options={TEMPLATE_FAMILY_OPTIONS.map((f) => ({
                value: f,
                label: f,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Language"
            name="template_language"
            rules={[{ required: true, message: 'Please select a language' }]}
          >
            <LanguageSelect />
          </Form.Item>
          <Form.Item
            label="Template"
            name="template_id"
            rules={[{ required: true, message: 'Please select a template' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!selectedType}
              loading={
                letterTemplatesQuery.loading ||
                emailTemplatesQuery.loading ||
                smsTemplatesQuery.loading
              }
              options={templateOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NetworkApprovedTemplates;
